import React from 'react';
import { Autocomplete, Chip, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

export const employeeDisplayName = (emp) => {
  if (!emp) return '';
  const full = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
  return full || emp.username || `#${emp.id}`;
};

// Converts a truck's additional_crew payload ({ user_id, first_name, ... })
// into the { id, first_name, ... } shape the employee lookup uses.
export const additionalCrewFromTruck = (truck) => (truck?.additional_crew || []).map((member) => ({
  id: member.user_id,
  first_name: member.first_name,
  last_name: member.last_name,
}));

/**
 * Multi-select for a truck's additional crew: people who are not the driver
 * or passenger but can also open this truck's list in Crew Mode (e.g. sent
 * over to pick up sites in winter). Value is an array of employee objects.
 */
const AdditionalCrewField = ({
  employees,
  value,
  onChange,
  driverId,
  passengerId,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const seatedIds = [driverId, passengerId].filter(Boolean).map(String);
  const isSeated = (emp) => seatedIds.includes(String(emp.id));

  // Keep already-assigned people selectable even if the lookup omits them,
  // so saving never silently drops someone.
  const byId = new Map();
  [...employees, ...value].forEach((emp) => {
    if (emp && !byId.has(String(emp.id))) byId.set(String(emp.id), emp);
  });
  const options = [...byId.values()].filter((emp) => !isSeated(emp));
  const selected = value.filter((emp) => !isSeated(emp));

  return (
    <Autocomplete
      multiple
      filterSelectedOptions
      disabled={disabled}
      options={options}
      value={selected}
      onChange={(_, next) => onChange(next)}
      getOptionLabel={employeeDisplayName}
      isOptionEqualToValue={(option, val) => String(option.id) === String(val.id)}
      noOptionsText={t('routeSetup.additionalCrewNoOptions')}
      renderTags={(tags, getTagProps) => tags.map((emp, index) => {
        const { key, ...tagProps } = getTagProps({ index });
        return (
          <Chip
            key={key}
            {...tagProps}
            label={employeeDisplayName(emp)}
            size="small"
          />
        );
      })}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('routeSetup.additionalCrew')}
          placeholder={selected.length === 0 ? t('routeSetup.additionalCrewPlaceholder') : ''}
          helperText={t('routeSetup.additionalCrewHelp')}
          inputProps={{
            ...params.inputProps,
            'aria-label': t('routeSetup.additionalCrew'),
          }}
        />
      )}
    />
  );
};

export default AdditionalCrewField;
